// Anke, 2026-07-21 — Gruppieren und Auflösen.
//
// Reine Funktionen über einer flachen Objektliste. Bewusst ohne BoardState und
// ohne Konva: Die Hierarchie-Regeln sind der knifflige Teil dieser Funktion, und
// sie gehören dorthin, wo man sie ohne Board prüfen kann.
//
// Modell (mit Anke entschieden): Die Liste bleibt FLACH. Jedes Objekt merkt sich
// per `groupId`, zu welcher Gruppe es gehört. Eine Gruppe ist selbst ein
// Board-Objekt (`type: 'group'`) und kann darum ebenfalls ein `groupId` tragen —
// daher entsteht Verschachtelung aus der Verweiskette, nicht aus verschachtelten
// Daten. Die öffentliche `config`-Schnittstelle bleibt damit unverändert.

import type { BoardObject, GroupObject, LineAnchor } from '../interfaces/board-object.interface.js';
import { isLine } from '../../features/line/line.types.js';
import { translateLine } from '../../features/line/line-translate.helper.js';
import type { LineObject } from '../../features/line/line.interface.js';

/** Ist das Objekt eine Gruppe? */
export function isGroup(obj: BoardObject): obj is GroupObject {
  return obj.type === 'group';
}

/**
 * Der oberste Vorfahre eines Objekts — die Gruppe, die beim Anklicken gewählt
 * wird, oder das Objekt selbst, wenn es zu keiner Gruppe gehört.
 *
 * Anke, 2026-07-21: "ganze Gruppe wird gewählt". Bei Verschachtelung ist das die
 * ÄUSSERSTE Gruppe — von aussen betrachtet ist die Verschachtelung unsichtbar,
 * man greift immer das Ganze.
 *
 * Gegen kaputte Daten abgesichert: Eine Verweisschleife (a gehört zu b, b zu a)
 * würde sonst ewig laufen. Kann durch beschädigte gespeicherte Boards entstehen,
 * darum hier abgefangen statt vorausgesetzt.
 */
export function outermostGroupId(
  objectId: string,
  byId: ReadonlyMap<string, BoardObject>,
): string {
  let current = objectId;
  const seen = new Set<string>([current]);
  for (;;) {
    const parent = byId.get(current)?.groupId;
    if (parent === undefined || !byId.has(parent) || seen.has(parent)) return current;
    seen.add(parent);
    current = parent;
  }
}

/**
 * Alle Mitglieder einer Gruppe, rekursiv — also auch die Mitglieder verschachtelter
 * Untergruppen. Die Gruppe selbst ist NICHT enthalten.
 */
export function groupMembers(
  groupId: string,
  objects: ReadonlyArray<BoardObject>,
): BoardObject[] {
  const direct = objects.filter((o) => o.groupId === groupId);
  return direct.flatMap((o) => (isGroup(o) ? [o, ...groupMembers(o.id, objects)] : [o]));
}

/**
 * Die Bounding-Box über eine nicht-leere Objektliste. Wird der Gruppe als
 * `x/y/width/height` mitgegeben, damit alles, was eine Box erwartet
 * (Auswahlrahmen, Ausrichten, "auf alles zoomen"), ohne Sonderfall weiterläuft.
 */
export function boundingBox(
  objects: ReadonlyArray<BoardObject>,
): { x: number; y: number; width: number; height: number } {
  const left = Math.min(...objects.map((o) => o.x));
  const top = Math.min(...objects.map((o) => o.y));
  const right = Math.max(...objects.map((o) => o.x + o.width));
  const bottom = Math.max(...objects.map((o) => o.y + o.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * Wer würde durch Gruppieren der aktuellen Auswahl zu Mitgliedern?
 *
 * Der Kern von Ankes Punkt 2 ("Gruppe plus weitere Elemente erneut gruppieren →
 * Gruppe in der Gruppe"): Gruppiert wird immer über die ÄUSSERSTEN Vorfahren der
 * Auswahl. Wer eine Gruppe anklickt, meint die Gruppe als Ganzes — nicht deren
 * Einzelteile. Ohne diesen Schritt würde ein Mitglied aus seiner alten Gruppe
 * herausgerissen statt die alte Gruppe als Einheit aufgenommen.
 *
 * Doppelte fallen weg: Zwei Geschwister derselben Gruppe ergeben EINEN Vorfahren.
 */
export function groupingCandidates(
  selectedIds: ReadonlyArray<string>,
  byId: ReadonlyMap<string, BoardObject>,
): string[] {
  const roots = new Set<string>();
  for (const id of selectedIds) {
    if (byId.has(id)) roots.add(outermostGroupId(id, byId));
  }
  return [...roots];
}

/**
 * Lässt sich die Auswahl gruppieren? Erst ab zwei EIGENSTÄNDIGEN Teilen —
 * zwei Geschwister derselben Gruppe sind bereits gruppiert, da gäbe es nichts
 * zu tun.
 */
export function canGroup(
  selectedIds: ReadonlyArray<string>,
  byId: ReadonlyMap<string, BoardObject>,
): boolean {
  return groupingCandidates(selectedIds, byId).length >= 2;
}

/**
 * Welche Gruppen löst der Auflösen-Knopf als Nächstes auf?
 *
 * Ankes Punkt 3: "die erste Hierarchieebene wird aufgelöst, beim nächsten Druck
 * die jetzt erste". Genau das ergibt sich, wenn immer die ÄUSSERSTEN Gruppen der
 * Auswahl fallen: Danach sind deren Untergruppen die neuen äussersten, und der
 * nächste Druck erwischt sie.
 */
export function ungroupTargets(
  selectedIds: ReadonlyArray<string>,
  byId: ReadonlyMap<string, BoardObject>,
): string[] {
  const targets = new Set<string>();
  for (const id of selectedIds) {
    const outer = outermostGroupId(id, byId);
    const obj = byId.get(outer);
    if (obj && isGroup(obj)) targets.add(outer);
  }
  return [...targets];
}

/** Ist in der Auswahl mindestens eine Gruppe, die aufgelöst werden könnte? */
export function canUngroup(
  selectedIds: ReadonlyArray<string>,
  byId: ReadonlyMap<string, BoardObject>,
): boolean {
  return ungroupTargets(selectedIds, byId).length > 0;
}

/**
 * Die Auswahl auf alles ausweiten, was mitgehen muss: zu jeder ausgewählten
 * Gruppe deren sämtliche Mitglieder, rekursiv.
 *
 * Anke, 2026-07-21 — nötig für Löschen, Duplizieren und Kopieren. Ohne das
 * würde beim Löschen einer Gruppe nur die Klammer verschwinden und die
 * Mitglieder blieben verwaist liegen: unsichtbar gruppiert zu etwas, das es
 * nicht mehr gibt.
 */
export function expandToMembers(
  selectedIds: ReadonlyArray<string>,
  objects: ReadonlyArray<BoardObject>,
): string[] {
  const out = new Set<string>();
  const byId = new Map(objects.map((o) => [o.id, o]));
  for (const id of selectedIds) {
    const obj = byId.get(id);
    if (!obj) continue;
    out.add(id);
    if (isGroup(obj)) for (const m of groupMembers(id, objects)) out.add(m.id);
  }
  return [...out];
}

/**
 * Objekte klonen und dabei die Gruppen-Verweise MITZIEHEN: Ein Klon zeigt auf
 * den Klon der Gruppe, nicht auf das Original.
 *
 * Ohne diese Umschreibung würden Duplikate und eingefügte Objekte in die
 * URSPRÜNGLICHE Gruppe wandern — sichtbar daran, dass sich das Original beim
 * Verschieben plötzlich mitbewegt.
 *
 * Verweise auf Gruppen, die nicht mitkopiert wurden (etwa beim Kopieren eines
 * einzelnen Mitglieds), fallen weg: Der Klon gehört dann zu keiner Gruppe. Ihn
 * an die Gruppe des Originals zu hängen, wäre die schlimmere Überraschung.
 *
 * Linien (Anke, 2026-10-03 — eine kopierte Linie war nicht mehr angedockt und
 * saß 20 px neben ihren Notizen):
 * - Sie werden STARR verschoben (`translateLine`: Rahmen, Endpunkte,
 *   Kurvenpunkte), nicht nur ihr Rahmen.
 * - Ihre Andock-Verweise (`startAnchor`/`endAnchor`) werden wie `groupId` auf
 *   die Klone umgeschrieben. Ist das Ziel eines Endes nicht mitkopiert, fällt
 *   der Anker weg (Ankes Entscheidung): Die Kopie ist an diesem Ende frei.
 */
export function cloneWithGroups(
  objects: ReadonlyArray<BoardObject>,
  newId: () => string,
  offset: { x: number; y: number },
): BoardObject[] {
  const idMap = new Map<string, string>();
  for (const o of objects) idMap.set(o.id, newId());
  return objects.map((o) => {
    const moved = isLine(o)
      ? cloneLineAnchors(translateLine(o, offset.x, offset.y), idMap)
      : { ...o, x: o.x + offset.x, y: o.y + offset.y };
    const clone = { ...moved, id: idMap.get(o.id)! } as BoardObject & { groupId?: string };
    const mappedParent = o.groupId ? idMap.get(o.groupId) : undefined;
    if (mappedParent) clone.groupId = mappedParent;
    else delete clone.groupId;
    return clone;
  });
}

/** Die Anker einer Linie auf die Klone ihrer Ziele umschreiben. Ein Anker, dessen
 *  Ziel nicht mitkopiert wurde, fällt weg; das Ende bleibt, wohin es verschoben wurde. */
function cloneLineAnchors(line: LineObject, idMap: ReadonlyMap<string, string>): LineObject {
  const { startAnchor, endAnchor, ...rest } = line;
  const remap = (anchor: LineAnchor | undefined): LineAnchor | undefined => {
    const target = anchor ? idMap.get(anchor.objectId) : undefined;
    return anchor && target ? { ...anchor, objectId: target } : undefined;
  };
  const start = remap(startAnchor);
  const end = remap(endAnchor);
  return { ...rest, ...(start ? { startAnchor: start } : {}), ...(end ? { endAnchor: end } : {}) };
}

/**
 * Was ein Klick auf `id` auswählen soll: alle ZEICHENBAREN Mitglieder seiner
 * äussersten Gruppe — oder das Objekt selbst, wenn es zu keiner gehört.
 *
 * Anke, 2026-07-21 (Fehlerbericht "die Elemente verschieben sich noch einzeln"):
 * Der erste Versuch legte die GRUPPE in die Auswahl. Die wird aber nie
 * gezeichnet und hat darum keinen Knoten auf der Bühne — der Auswahlrahmen griff
 * ins Leere, und das Mitziehen sah nur EIN ausgewähltes Element und bewegte
 * folglich nur das angefasste.
 *
 * Deshalb steht in der Auswahl jetzt, was man auch anfassen kann. Die Gruppe
 * bleibt reine Buchführung: Sie hält fest, WAS zusammengehört, und die Auswahl
 * sorgt dafür, dass es sich auch zusammen verhält.
 */
export function clickSelectionFor(
  id: string,
  objects: ReadonlyArray<BoardObject>,
): string[] {
  const byId = new Map(objects.map((o) => [o.id, o]));
  const outer = outermostGroupId(id, byId);
  const obj = byId.get(outer);
  if (!obj || !isGroup(obj)) return [outer];
  const leaves = groupMembers(outer, objects).filter((o) => !isGroup(o)).map((o) => o.id);
  // Eine Gruppe ohne zeichenbare Mitglieder darf nicht zu einer LEEREN Auswahl
  // führen — dann liesse sich nichts mehr anklicken.
  return leaves.length > 0 ? leaves : [outer];
}

/**
 * Alles, was mitgehen muss, wenn die Auswahl gelöscht/kopiert/dupliziert wird —
 * einschliesslich der Gruppen-Klammern DARÜBER.
 *
 * Nach oben zu laufen ist nötig, seit in der Auswahl die Mitglieder stehen statt
 * der Gruppe: Ohne diesen Schritt bliebe beim Löschen die leere Klammer zurück,
 * und ein Duplikat verlöre seine Gruppenstruktur.
 *
 * Eine Klammer kommt nur mit, wenn ALLE ihre Mitglieder mitgehen — sonst würde
 * das Kopieren eines einzelnen Teils die Gruppe der Übrigen zerstören.
 *
 * Das Ergebnis steht in DOKUMENT-Reihenfolge (Position in `objects`), NICHT in
 * der Reihenfolge, in der die Objekte angeklickt wurden. Anke, 2026-07-22
 * (Fehlerbericht): Kopieren/Einfügen vergibt den z-index in genau dieser
 * Reihenfolge — kam sie aus der Klick-Folge, hing die Stapelung der Kopien
 * davon ab, WIE man ausgewählt hatte. `objects` ist die Wahrheit über die
 * Stapelreihenfolge (`allObjects()` liefert sie z-sortiert), also hier sortieren.
 */
export function expandForTransfer(
  selectedIds: ReadonlyArray<string>,
  objects: ReadonlyArray<BoardObject>,
): string[] {
  const out = new Set(expandToMembers(selectedIds, objects));
  for (;;) {
    const before = out.size;
    for (const group of objects.filter(isGroup)) {
      if (out.has(group.id)) continue;
      const members = objects.filter((o) => o.groupId === group.id);
      if (members.length > 0 && members.every((m) => out.has(m.id))) out.add(group.id);
    }
    if (out.size === before) break;
  }
  // In Dokument-Reihenfolge ausgeben, damit sie unabhängig von der Klick-Folge ist.
  return objects.filter((o) => out.has(o.id)).map((o) => o.id);
}

/**
 * Die neue Auswahl nach einem Umschalt-Klick (Shift/Strg) auf eine Einheit.
 *
 * Anke, 2026-07-22 — ein Umschalt-Klick auf ein bereits ausgewähltes Objekt
 * wählt es wieder ab. `unitIds` sind die zeichenbaren Mitglieder der geklickten
 * Einheit (bei einer Gruppe alle, sonst das eine Objekt). Ist die Einheit
 * KOMPLETT ausgewählt, fällt sie heraus; sonst wird sie ergänzt — ein teilweise
 * gewähltes Set vervollständigt sich also, statt sich abzuwählen.
 *
 * Reihenfolge-stabil: Ergänzungen hängen hinten an, damit die Stapel-/
 * Dokumentreihenfolge an anderer Stelle (z. B. Kopieren) unberührt bleibt.
 */
export function toggleUnitSelection(
  current: ReadonlyArray<string>,
  unitIds: ReadonlyArray<string>,
): string[] {
  const unit = new Set(unitIds);
  const fullySelected = unitIds.every((m) => current.includes(m));
  return fullySelected
    ? current.filter((x) => !unit.has(x))
    : [...new Set([...current, ...unitIds])];
}
